import appleIcon from '@/assets/figma/login-apple.svg';
import googleIcon from '@/assets/figma/login-google.svg';
import kakaoIcon from '@/assets/figma/login-kakao.svg';
import naverIcon from '@/assets/figma/login-naver.svg';
import tooltipArrow from '@/assets/figma/login-tooltip-arrow.svg';
import type { SocialProvider } from '@/entities/auth/api/auth.api';
import {
  SOCIAL_PROVIDER_LABEL,
  startSocialLogin,
} from '@/entities/auth/social/socialRedirect';
import { cn } from '@/shared/lib/utils';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

/** 마지막으로 성공한 수단 — 브라우저에만 저장하고 서버에 올리지 않는다 */
export const LAST_PROVIDER_KEY = 'rudalink-web:last-login-provider';

/** 네이버 → Apple → 카카오 → Google (Figma 833:7543 순서) */
const PROVIDERS: Array<{
  id: SocialProvider;
  className: string;
  icon: { src: string; size: number };
}> = [
  {
    id: 'naver',
    className: 'bg-[#03a94d]',
    icon: { src: naverIcon, size: 20 },
  },
  // Apple 아이콘은 검정 원까지 그려진 48px 에셋이다
  { id: 'apple', className: '', icon: { src: appleIcon, size: 48 } },
  {
    id: 'kakao',
    className: 'bg-[#fee500]',
    icon: { src: kakaoIcon, size: 24 },
  },
  {
    id: 'google',
    className: 'border border-[rgba(116,119,117,0.15)] bg-white',
    icon: { src: googleIcon, size: 24 },
  },
];

/** 로그인에 성공한 수단을 기록한다. 이메일로 성공하면 SNS 툴팁은 사라진다 (M-01) */
// eslint-disable-next-line react-refresh/only-export-components
export const rememberLastProvider = (provider: SocialProvider | 'email') => {
  try {
    localStorage.setItem(LAST_PROVIDER_KEY, provider);
  } catch {
    // 저장소를 못 쓰는 환경이면 툴팁만 안 보인다
  }
};

const readLastProvider = (): string | null => {
  try {
    return localStorage.getItem(LAST_PROVIDER_KEY);
  } catch {
    return null;
  }
};

/** "최근 로그인" 툴팁 (Figma 864:10570) — 아이콘 아래 8px에 가운데 맞춘다 */
const RecentTooltip = () => (
  <span className="pointer-events-none absolute top-full left-1/2 mt-2 flex -translate-x-1/2 flex-col items-center">
    <img
      src={tooltipArrow}
      alt=""
      width={14}
      height={9}
      className="mb-[-0.5px]"
    />
    <span className="rounded-full bg-black/80 px-3 py-1 text-[13px] leading-5 font-medium tracking-[-0.065px] whitespace-nowrap text-white">
      최근 로그인
    </span>
  </span>
);

export const SocialLoginButtons = ({
  className,
  pendingProvider,
  onPendingChange,
  onError,
  redirectTo,
}: {
  className?: string;
  pendingProvider: string | null;
  onPendingChange: (provider: string | null) => void;
  /** 인가 화면으로 보내지 못했을 때 (클라이언트 ID 미설정 등) — X1 */
  onError: (provider: SocialProvider) => void;
  /** 로그인 후 돌아갈 내부 경로 — 콜백까지 들고 간다 */
  redirectTo?: string;
}) => {
  const lastProvider = readLastProvider();

  // 제공자 화면에서 뒤로 가기로 돌아오면 bfcache가 진행 중 상태를 그대로 복원하므로 푼다
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) =>
      e.persisted && onPendingChange(null);
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [onPendingChange]);

  /** 제공자 화면으로 이동한다. 결과는 /auth/callback/{provider}에서 처리한다 */
  const handleClick = async (provider: SocialProvider) => {
    onPendingChange(provider);
    try {
      await startSocialLogin(provider, redirectTo);
    } catch {
      onPendingChange(null);
      onError(provider);
    }
  };

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      <div className="flex w-full max-w-[360px] items-center gap-2 px-4">
        <span className="h-px flex-1 bg-[#e5e7ea]" />
        <span className="text-sm leading-[22px] tracking-[-0.112px] whitespace-nowrap text-[#6d717f]">
          SNS 계정으로 로그인
        </span>
        <span className="h-px flex-1 bg-[#e5e7ea]" />
      </div>

      <div className="flex items-center gap-4">
        {PROVIDERS.map((p) => {
          const isPending = pendingProvider === p.id;
          return (
            <div key={p.id} className="relative">
              <button
                type="button"
                aria-label={`${SOCIAL_PROVIDER_LABEL[p.id]}로 로그인`}
                disabled={pendingProvider !== null}
                onClick={() => void handleClick(p.id)}
                className={cn(
                  'relative flex size-12 items-center justify-center rounded-full disabled:opacity-60',
                  p.className,
                )}
              >
                <img
                  src={p.icon.src}
                  alt=""
                  width={p.icon.size}
                  height={p.icon.size}
                />
                {isPending && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                    <Loader2
                      className="size-5 animate-spin text-white"
                      aria-hidden
                    />
                  </span>
                )}
              </button>
              {lastProvider === p.id && <RecentTooltip />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
